import React, { useState, useEffect } from "react";
import axios from "axios";
import { Container, TextField, Button, Typography, Box, CircularProgress, Paper, Alert, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { saveNotification } from "../utils/notifications.jsx";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const AdvanceSalaryRequest = () => {
  const [form, setForm] = useState({
    name: "",
    employee_id: "",
    department: "",
    salaryAmount: "",
    candidateEmail: "",    
    hrEmail: "",           
    reason: "",
    designation: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [fetchingEmployeeId, setFetchingEmployeeId] = useState(true);
  const [fetchingHrEmail, setFetchingHrEmail] = useState(false);
  const [hrManagers, setHrManagers] = useState([]);
  const [currencySymbol, setCurrencySymbol] = useState('₹');

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateSalaryAmount = (amount) => {
    const numAmount = parseFloat(amount);
    return !isNaN(numAmount) && numAmount > 0;
  };

  const validateForm = () => {
    const errors = {};

    // Name validation
    if (!form.name.trim()) {
      errors.name = "Name is required";
    } else if (form.name.trim().length < 2) {
      errors.name = "Name must be at least 2 characters";
    }

    // Employee ID validation
    if (!form.employee_id.trim()) {
      errors.employee_id = "Employee ID is required";
    }

    // Department validation
    if (!form.department.trim()) {
      errors.department = "Department is required";
    }

    // Salary amount validation
    if (!form.salaryAmount.trim()) {
      errors.salaryAmount = "Salary amount is required";
    } else if (!validateSalaryAmount(form.salaryAmount)) {
      errors.salaryAmount = "Please enter a valid positive number";
    }

    // Candidate email validation
    if (!form.candidateEmail.trim()) {
      errors.candidateEmail = "Candidate email is required";
    } else if (!validateEmail(form.candidateEmail)) {
      errors.candidateEmail = "Please enter a valid email address";
    }

    // HR email validation
    if (!form.hrEmail.trim()) {
      errors.hrEmail = "HR email is required";
    } else if (!validateEmail(form.hrEmail)) {
      errors.hrEmail = "Please enter a valid HR email address";
    }

    // Reason validation
    if (!form.reason.trim()) {
      errors.reason = "Reason is required";
    } else if (form.reason.trim().length < 10) {
      errors.reason = "Please provide a detailed reason (at least 10 characters)";
    }

    // Designation validation
    if (!form.designation.trim()) {
      errors.designation = "Designation is required";
    }

    return errors;
  };

  // Real-time validation on field change
  const validateField = (name, value) => {
    const fieldErrors = { ...validationErrors };
    
    switch (name) {
      case 'name':
        if (!value.trim()) {
          fieldErrors.name = "Name is required";
        } else if (value.trim().length < 2) {
          fieldErrors.name = "Name must be at least 2 characters";
        } else {
          delete fieldErrors.name;
        }
        break;
      
      case 'salaryAmount':
        if (!value.trim()) {
          fieldErrors.salaryAmount = "Salary amount is required";
        } else if (!validateSalaryAmount(value)) {
          fieldErrors.salaryAmount = "Please enter a valid positive number";
        } else {
          delete fieldErrors.salaryAmount;
        }
        break;
      
      case 'candidateEmail':
        if (!value.trim()) {
          fieldErrors.candidateEmail = "Candidate email is required";
        } else if (!validateEmail(value)) {
          fieldErrors.candidateEmail = "Please enter a valid email address";
        } else {
          delete fieldErrors.candidateEmail;
        }
        break;
      
      case 'hrEmail':
        if (!value.trim()) {
          fieldErrors.hrEmail = "HR email is required";
        } else if (!validateEmail(value)) {
          fieldErrors.hrEmail = "Please enter a valid HR email address";
        } else {
          delete fieldErrors.hrEmail;
        }
        break;
      
      case 'reason':
        if (!value.trim()) {
          fieldErrors.reason = "Reason is required";
        } else if (value.trim().length < 10) {
          fieldErrors.reason = "Please provide a detailed reason (at least 10 characters)";
        } else {
          delete fieldErrors.reason;
        }
        break;
      
      case 'department':
        if (!value.trim()) {
          fieldErrors.department = "Department is required";
        } else {
          delete fieldErrors.department;
        }
        break;
      
      case 'designation':
        if (!value.trim()) {
          fieldErrors.designation = "Designation is required";
        } else {
          delete fieldErrors.designation;
        }
        break;
      
      default:
        break;
    }
    
    setValidationErrors(fieldErrors);
  };

  // Fetch employee ID and basic details
  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        
        setFetchingEmployeeId(true);
        setError(""); // Clear any previous errors
        
        const response = await axios.get(`${API_URL}/api/user/current`, {withCredentials: true});
        
        if (!response.data.employee_id) {
          setError("Employee ID not found. Please contact administrator.");
          return;
        }

        const token = localStorage.getItem('token');
        if (token) {
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const company_id = payload.company_id;
            const cmpRes = await axios.get(`${API_URL}/api/cmp/${company_id}`, { headers: { Authorization: `Bearer ${token}` } });
            if (cmpRes.data.success && cmpRes.data.data?.currency_symbol) {
              setCurrencySymbol(cmpRes.data.data.currency_symbol);
            }
          } catch (e) {
            // console.error("Failed to fetch company country", e);
          }
        }
        
        // Set employee details
        setForm(prevForm => ({
          ...prevForm,
          employee_id: response.data.employee_id || "",
        }));
        
        // After getting employee ID, fetch the HR email
        await fetchHrEmail(response.data.employee_id);
        
      } catch (err) {
        const errorMessage = err.response?.data?.message || "Failed to fetch employee data. Please try again later.";
        setError(errorMessage);
      } finally {
        setFetchingEmployeeId(false);
      }
    };

    fetchEmployeeData();
  }, []);

  // Function to fetch HR email from the roles table
  const fetchHrEmail = async (employeeId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
  
      setFetchingHrEmail(true);
      
      // First, get the employee's department from employee details
      const employeeResponse = await axios.get(`${API_URL}/api/employees/${employeeId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      if (!employeeResponse.data.data) {
        throw new Error("Employee data not found");
      }
      
      const employeeData = employeeResponse.data.data;
      const employeeDepartment = employeeData.department;
      
      // Update department and other fields if available
      setForm(prevForm => ({
        ...prevForm,
        department: employeeDepartment || "",
        name: `${employeeData.firstName || ''} ${employeeData.lastName || ''}`.trim() || "",
        candidateEmail: employeeData.companyemail || ""
      }));
      
      // Get all HR Managers by role
      const hrResponse = await axios.get(`${API_URL}/api/roles/employees/byrole/HR Manager`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (hrResponse.data.success && hrResponse.data.data) {
        const hrs = hrResponse.data.data;
        setHrManagers(hrs);
        
        // Find HR Manager for this department
        const deptHr = hrs.find(hr => hr.department === employeeDepartment);
        if (deptHr) {
          setForm(prevForm => ({
            ...prevForm,
            hrEmail: deptHr.companyemail || ""
          }));
        } else if (hrs.length > 0) {
          // Fallback: first HR manager
          setForm(prevForm => ({
            ...prevForm,
            hrEmail: hrs[0].companyemail || ""
          }));
        }
      }
    } catch (err) {
      // Don't block form submission, but log the error
    } finally {
      setFetchingHrEmail(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    
    // Clear general error when user starts typing
    if (error) setError("");
    
    // Validate field in real-time
    validateField(name, value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setError(""); // Clear previous errors
    const errors = validateForm();
    setValidationErrors(errors);
  
    if (Object.keys(errors).length > 0) {
      setError("Please fix the validation errors before submitting.");
      return;
    }
  
    setIsLoading(true);
  
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
      const payload = JSON.parse(atob(token.split(".")[1]));
      const company_id = payload.company_id;  // ✅ extract company_id
  
      const response = await axios.post(
        `${API_URL}/api/salary-request`,  // ✅ send company_id in query
        form,
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json",
                      Authorization: `Bearer ${token}` }
           }
        
      );
  
      if (response.status === 200 || response.status === 201) {
        saveNotification({
          id: Date.now(),
          message: "New Salary Advance Request Submitted",
          date: new Date().toISOString(),
          link: "/salaryrequest",
          read: false
        });
  
        alert("Request submitted successfully!");
  
        const { employee_id, hrEmail, department, candidateEmail, name } = form;
        setForm({
          name,
          employee_id,
          department,
          salaryAmount: "",
          candidateEmail,
          hrEmail,
          reason: "",
          designation: ""
        });
  
        setValidationErrors({});
      } else {
        throw new Error("Unexpected response from server");
      }
    } catch (err) {
      let errorMessage = "Failed to submit request. Please try again.";
  
      if (err.response) {
        if (err.response.status === 400) {
          errorMessage = err.response.data?.message || "Invalid data provided. Please check your inputs.";
        } else if (err.response.status === 401) {
          errorMessage = "Authentication failed. Please log in again.";
        } else if (err.response.status === 403) {
          errorMessage = "You don't have permission to submit this request.";
        } else if (err.response.status >= 500) {
          errorMessage = "Server error. Please try again later.";
        } else {
          errorMessage = err.response.data?.message || `Server error (${err.response.status})`;
        }
      } else if (err.request) {
        errorMessage = "Network error. Please check your connection and try again.";
      }
  
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };
  
  
  return (
    <Container maxWidth="1500px" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ 
        p: 4,  
        borderRadius: 2,
        border: '2px solid',
        borderColor: 'grey.300',
        boxShadow: 4,  
      }}>
        <Typography variant="h4"  gutterBottom fontWeight="bold">
          Advance Salary Request
        </Typography>
        
        {/* Display general error */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {fetchingEmployeeId ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading your details...</Typography>
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField 
                fullWidth 
                margin="normal" 
                label="Name" 
                name="name" 
                value={form.name} 
                onChange={handleChange} 
                required 
                error={!!validationErrors.name}
                helperText={validationErrors.name}
              />
              <TextField 
                fullWidth 
                margin="normal" 
                label="Employee ID" 
                name="employee_id" 
                value={form.employee_id} 
                InputProps={{ readOnly: true }}
                sx={{ 
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "#000",
                    backgroundColor: "#f5f5f5"
                  }
                }}
              />
            </Box>
            
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField 
                fullWidth 
                margin="normal"
                label="Candidate Email" 
                name="candidateEmail" 
                type="email"
                value={form.candidateEmail} 
                onChange={handleChange} 
                required 
                error={!!validationErrors.candidateEmail}
                helperText={validationErrors.candidateEmail}
              />
              <FormControl fullWidth margin="normal" required error={!!validationErrors.hrEmail}>
                <InputLabel id="hr-email-label">HR Email</InputLabel>
                <Select
                  labelId="hr-email-label"
                  name="hrEmail"
                  value={form.hrEmail}
                  onChange={handleChange}
                  label="HR Email"
                  endAdornment={fetchingHrEmail ? <CircularProgress size={20} sx={{ mr: 4 }} /> : null}
                >
                  {hrManagers.map((hr) => (
                    <MenuItem key={hr.companyemail} value={hr.companyemail}>
                      {hr.companyemail}
                    </MenuItem>
                  ))}
                  {hrManagers.length === 0 && form.hrEmail && (
                    <MenuItem value={form.hrEmail}>{form.hrEmail}</MenuItem>
                  )}
                </Select>
                {validationErrors.hrEmail && (
                  <Typography variant="caption" color="error.main" sx={{ mt: 0.5, ml: 1.5 }}>
                    {validationErrors.hrEmail}
                  </Typography>
                )}
              </FormControl>
            </Box>
            
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField 
                fullWidth 
                margin="normal" 
                label="Department" 
                name="department" 
                value={form.department} 
                onChange={handleChange} 
                required 
                error={!!validationErrors.department}
                helperText={validationErrors.department}
              />
              <TextField 
                fullWidth 
                margin="normal" 
                label={`Salary Amount Requested (${currencySymbol})`} 
                name="salaryAmount" 
                type="number"
                inputProps={{ min: "0", step: "0.01" }}
                value={form.salaryAmount} 
                onChange={handleChange} 
                required 
                error={!!validationErrors.salaryAmount}
                helperText={validationErrors.salaryAmount}
              />
            </Box>
            
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField 
                fullWidth 
                margin="normal" 
                label="Reason" 
                name="reason" 
                multiline
                rows={3}
                value={form.reason} 
                onChange={handleChange} 
                required 
                error={!!validationErrors.reason}
                helperText={validationErrors.reason}
                placeholder="Please provide a detailed reason for the advance salary request"
              />
              <TextField 
                fullWidth 
                margin="normal" 
                label="Designation" 
                name="designation" 
                value={form.designation} 
                onChange={handleChange} 
                required 
                error={!!validationErrors.designation}
                helperText={validationErrors.designation}
              />
            </Box>
            
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                sx={{ width: "120px" }} 
                disabled={isLoading || Object.keys(validationErrors).length > 0}
              >
                {isLoading ? <CircularProgress size={24} /> : "Submit"}
              </Button>
            </Box>
            
            <Alert severity="info" sx={{ mt: 2 }}>
              Please apply at least 3 days in advance
            </Alert>
          </form>
        )}
      </Paper>
    </Container>
  );
};

export default AdvanceSalaryRequest;